// components/categories/CategoriesSection.tsx
'use client';

import React, { useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { categoriesData } from '@/data/categories';
import { CategoryCard } from './CategoryCard';
import { StatsBar } from './StatsBar';
import { CTAButton } from './CTAButton';
import { CornerBracket } from './CornerBracket';

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger, useGSAP);
}

const WORDS = ['Explore', 'Our', 'Categories'] as const;

export const CategoriesSection: React.FC = () => {
  const sectionRef = useRef<HTMLElement | null>(null);
  const cardsRef = useRef<HTMLDivElement | null>(null);
  const eyebrowRef = useRef<HTMLSpanElement | null>(null);
  const headingRef = useRef<HTMLDivElement | null>(null);
  const lineRef = useRef<HTMLDivElement | null>(null);
  const subRef = useRef<HTMLParagraphElement | null>(null);
  const headerBoxRef = useRef<HTMLDivElement | null>(null);

  useGSAP(
    () => {
      const headerTL = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 78%',
          toggleActions: 'play none none reverse',
        },
      });

      // Header box brackets animate in
      if (headerBoxRef.current) {
        headerTL.fromTo(
          headerBoxRef.current.querySelectorAll('[data-bracket]'),
          { scale: 0, opacity: 0 },
          { scale: 1, opacity: 1, duration: 0.45, stagger: 0.07, ease: 'back.out(2.5)' }
        );
      }

      headerTL
        .fromTo(
          eyebrowRef.current,
          { opacity: 0, y: 14, letterSpacing: '0.5em' },
          { opacity: 1, y: 0, letterSpacing: '0.28em', duration: 0.65, ease: 'power3.out' },
          '-=0.2'
        )
        .fromTo(
          headingRef.current ? Array.from(headingRef.current.children) : [],
          { opacity: 0, y: 32, rotateX: -50, filter: 'blur(5px)' },
          {
            opacity: 1, y: 0, rotateX: 0, filter: 'blur(0px)',
            duration: 0.85, stagger: 0.1, ease: 'power4.out',
          },
          '-=0.35'
        )
        .fromTo(
          lineRef.current,
          { scaleX: 0, opacity: 0 },
          { scaleX: 1, opacity: 1, duration: 0.7, ease: 'power2.inOut', transformOrigin: 'center' },
          '-=0.4'
        )
        .fromTo(
          subRef.current,
          { opacity: 0, y: 10 },
          { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out' },
          '-=0.3'
        );

      // Cards stagger in
      if (cardsRef.current) {
        gsap.fromTo(
          Array.from(cardsRef.current.children),
          { opacity: 0, y: 44, scale: 0.95 },
          {
            opacity: 1, y: 0, scale: 1,
            duration: 0.9,
            stagger: { amount: 0.55, from: 'start' },
            ease: 'power3.out',
            scrollTrigger: {
              trigger: cardsRef.current,
              start: 'top 82%',
              toggleActions: 'play none none reverse',
            },
          }
        );
      }
    },
    { scope: sectionRef }
  );

  return (
    <section
      ref={sectionRef}
      aria-labelledby="categories-heading"
      className="relative w-full bg-[#FEFDFB] py-16 sm:py-24 px-4 xs:px-6 flex justify-center overflow-hidden"
    >
      {/* Subtle background grid */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.022]"
        style={{
          backgroundImage: [
            'linear-gradient(#1C1B19 1px, transparent 1px)',
            'linear-gradient(90deg, #1C1B19 1px, transparent 1px)',
          ].join(','),
          backgroundSize: '52px 52px',
        }}
      />

      <div className="relative w-full max-w-[1400px]">

        {/* ── Header ── */}
        <div className="text-center mb-14 xs:mb-18" style={{ perspective: 700 }}>

          {/* Bracketed eyebrow — brackets always visible, animate in on scroll */}
          <div ref={headerBoxRef} className="relative inline-block px-12 py-5 mb-3">
            {(['tl', 'tr', 'bl', 'br'] as const).map((c) => {
              const isTop = c === 'tl' || c === 'tr';
              const isLeft = c === 'tl' || c === 'bl';
              return (
                <span
                  key={c}
                  data-bracket={c}
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    width: 18,
                    height: 18,
                    ...(isTop ? { top: 0 } : { bottom: 0 }),
                    ...(isLeft ? { left: 0 } : { right: 0 }),
                    borderTopWidth: isTop ? 1.5 : 0,
                    borderBottomWidth: !isTop ? 1.5 : 0,
                    borderLeftWidth: isLeft ? 1.5 : 0,
                    borderRightWidth: !isLeft ? 1.5 : 0,
                    borderStyle: 'solid',
                    borderColor: '#B08D57',
                    transformOrigin: `${isTop ? 'top' : 'bottom'} ${isLeft ? 'left' : 'right'}`,
                  }}
                />
              );
            })}

            <span
              ref={eyebrowRef}
              className="inline-flex items-center gap-2.5 text-[10px] tracking-[0.28em] uppercase text-[#B08D57] font-semibold"
            >
              <span aria-hidden className="block w-4 h-px bg-[#B08D57]/50" />
              Curated Materials
              <span aria-hidden className="block w-4 h-px bg-[#B08D57]/50" />
            </span>
          </div>

          {/* Heading */}
          <div
            ref={headingRef}
            id="categories-heading"
            className="flex items-end justify-center gap-x-3 xs:gap-x-4 flex-wrap"
            style={{ perspective: 600 }}
          >
            {WORDS.map((word, i) => (
              <span
                key={word}
                className="inline-block leading-[1.0]"
                style={{
                  fontSize: i === 1
                    ? 'clamp(1.9rem, 4.8vw, 3.6rem)'
                    : 'clamp(2.2rem, 5.6vw, 4.2rem)',
                  fontWeight: 900,
                  letterSpacing: '-0.025em',
                  color: i === 1 ? '#B08D57' : '#1C1B19',
                  fontStyle: i === 1 ? 'italic' : 'normal',
                }}
              >
                {word}
              </span>
            ))}
          </div>

          {/* Gold divider */}
          <div
            ref={lineRef}
            aria-hidden
            className="mx-auto mt-5 mb-4"
            style={{
              width: 52,
              height: 1,
              background: 'linear-gradient(to right, transparent, #B08D57, transparent)',
            }}
          />

          {/* Sub-copy */}
          <p
            ref={subRef}
            className="text-[12px] xs:text-[13px] text-[#84796B] tracking-[0.06em]"
          >
            Premium quality products for every need
          </p>
        </div>

        {/* ── Card Grid ── */}
        {/* px-2 / py-2 so outside brackets don't clip at section edges */}
        <div
          ref={cardsRef}
          className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 sm:gap-8 px-2 py-2"
        >
          {categoriesData.map((category, index) => (
            <CategoryCard key={category.id} category={category} index={index} />
          ))}
        </div>

        <StatsBar />
        <CTAButton />
      </div>
    </section>
  );
};

export default CategoriesSection;